import { ImageFromServer } from '@/components/images/ImageFromServer';
import { ExerciseDTO } from '@/generated';
import { useEquipmentListQuery, useExerciseCategoriesQuery, useExerciseFactsQuery } from '@/hooks/query-hooks';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { findRelatedExercises } from '../exercise-utils';

export type CopyExerciseDataMode = 'equipment' | 'categories' | 'facts' | 'training' | 'links' | 'all';

type CopyExerciseDataModalProps = {
  show: boolean;
  mode: CopyExerciseDataMode;
  progressionExerciseIds: string[];
  regressionExerciseIds: string[];
  exercises: ExerciseDTO[];
  onHide: () => void;
  onCopy: (sourceExercise: ExerciseDTO, selectedIds: string[]) => void;
};

const labels: Record<CopyExerciseDataMode, string> = {
  equipment: 'equipment',
  categories: 'categories and difficulty',
  facts: 'exercise facts',
  training: 'training settings',
  links: 'links',
  all: 'exercise data',
};

export const allExerciseDataIds = ['equipment', 'prerequisites', 'supportExercises', 'facts', 'links', 'info', 'muscles', 'training'] as const;
export const difficultyCopyId = '__difficulty__';

export const CopyExerciseDataModal = (props: CopyExerciseDataModalProps) => {
  const [sourceExerciseId, setSourceExerciseId] = useState<string>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { equipmentList } = useEquipmentListQuery();
  const { exerciseCategories } = useExerciseCategoriesQuery(props.show && props.mode === 'categories');
  const { exerciseFacts } = useExerciseFactsQuery();
  const sourceExercises = useMemo(
    () => findRelatedExercises(props.progressionExerciseIds, props.regressionExerciseIds, props.exercises),
    [props.progressionExerciseIds, props.regressionExerciseIds, props.exercises],
  );
  const sourceExercise = sourceExercises.find((exercise) => exercise.id === sourceExerciseId);

  useEffect(() => {
    if (props.show) {
      setSourceExerciseId(undefined);
      setSelectedIds([]);
    }
  }, [props.show]);

  const getIds = (exercise: ExerciseDTO) => {
    if (props.mode === 'equipment') return exercise.equipment.map((item) => item.equipmentId);
    if (props.mode === 'categories') return [...exercise.categoryIds, difficultyCopyId];
    if (props.mode === 'facts') return exercise.exerciseFactIds ?? [];
    if (props.mode === 'links') return exercise.links.map((_, index) => String(index));
    if (props.mode === 'all') return [...allExerciseDataIds];
    return ['training'];
  };

  const selectSourceExercise = (exercise: ExerciseDTO) => {
    setSourceExerciseId(exercise.id);
    setSelectedIds(getIds(exercise));
  };

  const toggle = (id: string) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id]));
  };

  const selectableChip = (id: string, label: string, title?: string) => {
    const selected = selectedIds.includes(id);
    return (
      <Chip
        key={id}
        label={label}
        title={title}
        variant="outlined"
        onClick={() => toggle(id)}
        sx={{ borderColor: selected ? 'common.white' : 'divider', bgcolor: selected ? 'action.selected' : 'transparent' }}
      />
    );
  };

  const preview = () => {
    if (!sourceExercise) return <Typography color="text.secondary">Select an exercise on the left.</Typography>;

    if (props.mode === 'equipment') {
      return sourceExercise.equipment.length ? (
        sourceExercise.equipment.map((item) =>
          selectableChip(
            item.equipmentId,
            `${equipmentList?.find((equipment) => equipment.id === item.equipmentId)?.name ?? item.equipmentId}${item.optional ? ' · optional' : ''}`,
          ),
        )
      ) : (
        <Typography color="text.secondary">No equipment configured.</Typography>
      );
    }

    if (props.mode === 'categories') {
      return (
        <>
          {sourceExercise.categoryIds.map((id) => selectableChip(id, exerciseCategories?.find((category) => category.id === id)?.name ?? id))}
          {selectableChip(difficultyCopyId, `Difficulty: ${sourceExercise.difficulty}`)}
        </>
      );
    }

    if (props.mode === 'training') {
      const config = sourceExercise.config;
      const summary = [
        `Weight: ${config.weight ?? 0}`,
        config.executionType === 'reps' ? `Repetitions: ${config.value ?? 0}` : undefined,
        config.executionType === 'duration' ? `Duration: ${config.value ?? 0}s` : undefined,
        config.executionType === 'intervals' ? `${config.timing?.intervals.length ?? 0} intervals` : undefined,
        config.trackPerSide ? 'Limb tracking' : undefined,
      ]
        .filter(Boolean)
        .join(' · ');
      return selectableChip('training', summary);
    }

    if (props.mode === 'links') {
      return sourceExercise.links.length ? (
        sourceExercise.links.map((link, index) => selectableChip(String(index), link.title || link.uri, link.uri))
      ) : (
        <Typography color="text.secondary">No links configured.</Typography>
      );
    }

    if (props.mode === 'all') {
      const groupLabels: Record<(typeof allExerciseDataIds)[number], string> = {
        equipment: 'Equipment',
        prerequisites: 'Prerequisites',
        supportExercises: 'Support exercises',
        facts: 'Facts',
        links: 'Links',
        info: 'Info (categories and difficulty)',
        muscles: 'Muscles and body zone',
        training: 'Training settings',
      };
      return allExerciseDataIds.map((id) => selectableChip(id, groupLabels[id]));
    }

    const factIds = sourceExercise.exerciseFactIds ?? [];
    return factIds.length ? (
      factIds.map((id) => {
        const fact = exerciseFacts?.find((candidate) => candidate.id === id);
        return selectableChip(id, fact?.name ?? fact?.description ?? id, fact?.description);
      })
    ) : (
      <Typography color="text.secondary">No exercise facts configured.</Typography>
    );
  };

  return (
    <Dialog open={props.show} onClose={props.onHide} fullWidth maxWidth="md">
      <DialogTitle>Copy {labels[props.mode]}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(220px, 1fr) minmax(280px, 1.4fr)' }, gap: 3, minHeight: 300 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Progressions and regressions
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1 }}>
              {sourceExercises.map((exercise) => (
                <Paper
                  key={exercise.id}
                  component="button"
                  type="button"
                  aria-pressed={sourceExerciseId === exercise.id}
                  onClick={() => selectSourceExercise(exercise)}
                  variant="outlined"
                  sx={{
                    borderColor: sourceExerciseId === exercise.id ? 'common.white' : 'divider',
                    bgcolor: sourceExerciseId === exercise.id ? 'action.selected' : 'background.paper',
                    color: 'text.primary',
                    cursor: 'pointer',
                    p: 1,
                    minWidth: 0,
                  }}
                >
                  <ImageFromServer id={exercise.imageId} thumbnail alt="" sx={{ width: 72, height: 72, mx: 'auto', borderRadius: 1 }} />
                  <Typography variant="caption" component="div" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                    {exercise.name}
                  </Typography>
                </Paper>
              ))}
            </Box>
            {!sourceExercises.length && <Typography color="text.secondary">No progressions or regressions found.</Typography>}
          </Box>
          <Box sx={{ borderLeft: { sm: 1 }, borderColor: { sm: 'divider' }, pl: { sm: 3 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {sourceExercise ? `${labels[props.mode][0].toUpperCase()}${labels[props.mode].slice(1)} of ${sourceExercise.name}` : labels[props.mode]}
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {preview()}
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onHide}>Cancel</Button>
        <Button variant="contained" disabled={!sourceExercise || !selectedIds.length} onClick={() => sourceExercise && props.onCopy(sourceExercise, selectedIds)}>
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
};
